<script setup lang="ts">
import { computed, onMounted, onUnmounted } from 'vue'
import { AlertTriangle, CheckCircle2, Loader2 } from 'lucide-vue-next'
import { Badge } from '@/components/ui'
import { useTaskActivityStore } from '@/stores/taskActivityStore'

const TERMINAL_STATUSES = [
  'completed',
  'completed_with_errors',
  'failed',
  'cancelled',
  'dead_lettered',
]

const taskActivity = useTaskActivityStore()
let unsubscribe: (() => void) | null = null

const globalCount = computed(() =>
  Object.values(taskActivity.activeTasksByEntityType)
    .flat()
    .filter(task => !TERMINAL_STATUSES.includes(task.status)).length
)

const globalHasFailures = computed(() =>
  taskActivity.recentTasks.some(
    t => t.status === 'failed' || t.status === 'dead_lettered'
  )
)

const variant = computed(() => {
  if (globalHasFailures.value) return 'error'
  if (globalCount.value > 0) return 'warning'
  return 'muted'
})

const icon = computed(() => {
  if (globalHasFailures.value) return AlertTriangle
  if (globalCount.value > 0) return Loader2
  return CheckCircle2
})

const iconClass = computed(() => [
  'h-3 w-3',
  { 'animate-spin': globalCount.value > 0 && !globalHasFailures.value },
])

const label = computed(() => {
  if (globalHasFailures.value) {
    if (globalCount.value > 0) return `${globalCount.value} syncing • failures`
    return 'Failures pending'
  }
  if (globalCount.value > 0) return `${globalCount.value} syncing`
  return 'All clear'
})

onMounted(() => {
  unsubscribe = taskActivity.subscribeActivity({
    entityTypes: ['STOCK', 'ITEM', 'CUSTOMER', 'INVOICE', 'PAYMENT'],
    includeRecent: true,
    includeActive: true,
    includeSummary: false,
    recentLimit: 50,
  })
})

onUnmounted(() => {
  if (unsubscribe) {
    unsubscribe()
    unsubscribe = null
  }
})
</script>

<template>
  <router-link to="/app/tasks" class="header-active-task-badge">
    <Badge :variant="variant" size="sm">
      <component :is="icon" :class="iconClass" />
      {{ label }}
    </Badge>
  </router-link>
</template>

<style scoped>
.header-active-task-badge {
  display: inline-flex;
  align-items: center;
  text-decoration: none;
  color: inherit;
}
</style>
